import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  SimpleGrid,
  Spinner,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { Gamepad2, Plus, Zap, Search, Lock, Target } from 'lucide-react';
import { auth } from '../firebase';
import { tableService } from '../services/tableService';
import walletService from '../services/walletService';
import enhancedTableService from '../services/enhancedTableService';
import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';
import PokerButton from '../components/ui/PokerButton';
import PokerCard from '../components/ui/PokerCard';
import PokerTableCard, { TableData } from '../components/PokerTableCard';
import StakeGroupedView from '../components/lobby/StakeGroupedView';
import { POKER_COLORS } from '../theme/designSystem';
import { subscribeToFriendsAtTables, FriendAtTable } from '../services/socialService';
// ============================================================================
//  PHASE 2: Import FeaturedTablesSection

// ============================================================================
import FeaturedTablesSection from '../components/FeaturedTablesSection';
// ============================================================================
//  PHASE 2: Import Quick Match Service

// ============================================================================
import quickMatchService, { getMatchTypeInfo } from '../services/quickMatchService';

const TableBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const currentUser = auth.currentUser;

  const [playcoinTables, setPlaycoinTables] = useState<TableData[]>([]);
  const [pkcashTables, setPkcashTables] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [joiningTableId, setJoiningTableId] = useState<string | null>(null);
  
  //  Search & Filter state

  const [searchQuery, setSearchQuery] = useState('');

  //  NEW: Friends at tables state

  const [friendsAtTables, setFriendsAtTables] = useState<FriendAtTable[]>([]);
  
  //  Join Private Table modal

  const { isOpen: isPrivateModalOpen, onOpen: openPrivateModal, onClose: closePrivateModal } = useDisclosure();
  const [privateCode, setPrivateCode] = useState('');
  const [joiningPrivate, setJoiningPrivate] = useState(false);

  // Real-time listeners refs
  const playcoinUnsubscribe = useRef<any>(null);
  const pkcashUnsubscribe = useRef<any>(null);

  // ============================================================================
  //  PHASE 2: Geo restriction state for PSS Coins

  // ============================================================================
  const [isPKCashRestricted, setIsPKCashRestricted] = useState(false);

  // ============================================================================
  //  PHASE 2: Quick Match state

  // ============================================================================
  const [isQuickMatching, setIsQuickMatching] = useState(false);

  // ============================================================================
  // STAKE GROUPING - null = show grouped stake rows, set = drill-down to a
  // single stake's table grid. Matches the CoinPoker cash lobby pattern.
  // ============================================================================
  const [selectedStake, setSelectedStake] = useState<{
    smallBlind: number;
    bigBlind: number;
    currency: 'playcoins' | 'pkcash';
  } | null>(null);

  // Check geo restriction on mount (simplified - you may have existing geo logic)
  useEffect(() => {
    // If you have geoService, use it here
    // For now, default to allowing PSS Coins (false = not restricted)
    // Example: geoService.isPKCashBlocked().then(setIsPKCashRestricted);
    setIsPKCashRestricted(false);
  }, []);

  /**
   * Set up real-time Firestore listener for PlayCoins
   */
  const setupPlaycoinListener = useCallback(() => {
    if (playcoinUnsubscribe.current) {
      playcoinUnsubscribe.current();
    }

    playcoinUnsubscribe.current = tableService.listenToTablesByCurrency(
      'playcoins',
      (tables) => {
        console.log('PlayCoin tables updated:', tables.length);
        // Include featured tables so the stake-grouped view shows the
        // pre-made house tables (Micro, Low Stakes, etc.) alongside any
        // user-created ones. Still filter private (unlisted) and tournament
        // tables - tournaments live in /tournaments only.
        const publicTables = tables.filter((t: any) =>
          !t.isPrivate &&
          !t.isTournament &&
          !t.tournamentId
        );
        setPlaycoinTables(publicTables);
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to PlayCoin tables:', error);
        setLoading(false);
      }
    );
  }, []);

  /**
   * Set up real-time Firestore listener for PSS Coins
   */
  const setupPkcashListener = useCallback(() => {
    if (pkcashUnsubscribe.current) {
      pkcashUnsubscribe.current();
    }

    pkcashUnsubscribe.current = tableService.listenToTablesByCurrency(
      'pkcash',
      (tables) => {
        console.log('PSS Coins tables updated:', tables.length);
        // Featured tables stay in the lobby so they appear in the stake-
        // grouped view (matching CoinPoker's unified lobby). Only private
        // and tournament tables get filtered out here.
        const publicTables = tables.filter((t: any) =>
          !t.isPrivate &&
          !t.isTournament &&
          !t.tournamentId
        );
        setPkcashTables(publicTables);
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to PSS Coins tables:', error);
        setLoading(false);
      }
    );
  }, []);

  /**
   * Initialize real-time listeners
   */
  useEffect(() => {
    if (currentUser?.uid) {
      setupPlaycoinListener();
      setupPkcashListener();

      return () => {
        if (playcoinUnsubscribe.current) playcoinUnsubscribe.current();
        if (pkcashUnsubscribe.current) pkcashUnsubscribe.current();
      };
    }
  }, [currentUser?.uid, setupPlaycoinListener, setupPkcashListener]);

  //  NEW: Subscribe to friends at tables

  useEffect(() => {
    if (!currentUser?.uid) return;
    console.log('👥 [TableBrowser] Subscribing to friends at tables for:', currentUser.uid);
    const unsubscribe = subscribeToFriendsAtTables(currentUser.uid, (friends) => {
      console.log('👥 [TableBrowser] Received friends update:', friends.length, friends);
      setFriendsAtTables(friends);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  /**
   *  Filter tables by search query

   */
  const filterTables = (tables: TableData[]) => {
    if (!searchQuery.trim()) return tables;
    const query = searchQuery.toLowerCase();
    return tables.filter(table => 
      table.name?.toLowerCase().includes(query) ||
      `${table.blinds.small}/${table.blinds.big}`.includes(query)
    );
  };

  /**
   *  Handle joining a private table by code

   */
  const handleJoinPrivateTable = async () => {
    if (!currentUser?.uid || !privateCode.trim()) return;
    
    setJoiningPrivate(true);
    try {
      const result = await enhancedTableService.findPrivateTableByCode(privateCode.trim().toUpperCase());
      
      if (!result.success || !result.tableId) {
        toast({
          title: 'Invalid Code',

          description: result.error || 'No table found with this code.',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      // Navigate to the table
      closePrivateModal();
      setPrivateCode('');
      navigate(`/game/${result.tableId}`);
      
    } catch (error: any) {
      console.error('Error joining private table:', error);
      toast({
        title: 'Error',

        description: error.message || 'Failed to join private table',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setJoiningPrivate(false);
    }
  };

  /**
   * Handle join table with optimistic update
   */
  const handleJoinTable = async (tableId: string) => {
    if (!currentUser?.uid) return;

    try {
      setJoiningTableId(tableId);

      //  Get table info to check currency and min buy-in

      const table = await tableService.getTable(tableId);
      if (!table) {
        toast({
          title: 'Error',

          description: 'Table not found',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      //  Check balance for PSS Coins tables

      if (table.currency === 'pkcash') {
        const wallet = await walletService.getWallet(currentUser.uid);
        const pkCashBalance = wallet?.pkCash || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (pkCashBalance < minBuyIn) {
          toast({
            title: 'Insufficient PSS Coins',

            description: `You need at least $${minBuyIn} to join. You have $${pkCashBalance.toFixed(2)}.`,
            status: 'error',
            duration: 5000,
          });
          return;
        }
      }

      //  Check balance for PlayCoin tables

      if (table.currency === 'playcoins') {
        const wallet = await walletService.getWallet(currentUser.uid);
        const playCoinsBalance = wallet?.playCoins || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (playCoinsBalance < minBuyIn) {
          toast({
            title: 'Insufficient PlayCoins',

            description: `You need at least ${minBuyIn.toLocaleString()} to join. You have ${playCoinsBalance.toLocaleString()}.`,
            status: 'error',
            duration: 5000,
          });
          return;
        }
      }

      //  iOS Native App: Block joining voice/video tables in native iOS app

      if ((table as any).voiceChatEnabled && isIOSVoiceVideoBlocked()) {
        toast({
          title: 'Not Available in App',

          description: 'Voice & Video tables are not yet supported in the iOS app. Please open this table in Safari or Chrome.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      //  DON'T join table here - let GamePage handle it after buy-in confirmation

      // Just navigate to the game page
      navigate(`/game/${tableId}`);
    } catch (error) {
      console.error('Error joining table:', error);
      toast({
        title: 'Error',

        description: 'Failed to join table',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setJoiningTableId(null);
    }
  };

  // ============================================================================
  //  PHASE 2: Handler for featured table join

  // ============================================================================
  const handleFeaturedTableJoin = useCallback((tableId: string) => {
    console.log('🎯 [TableBrowser] Featured table join:', tableId);
    // The FeaturedTablesSection handles navigation internally
    // This callback is for any additional tracking/analytics
  }, []);

  // ============================================================================
  //  PHASE 2: Quick Match Handler

  // ============================================================================
  const handleQuickMatch = useCallback(async () => {
    if (!currentUser?.uid) {
      toast({
        title: 'Login Required',

        description: 'Please log in to quick match',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsQuickMatching(true);

    try {
      console.log('🎯 [TableBrowser] Starting Quick Match...');
      const result = await quickMatchService.findQuickMatchTable(currentUser.uid);

      if (result.success && result.tableId) {
        const matchInfo = getMatchTypeInfo(result.matchType);
        
        toast({
          title: `${matchInfo.icon} ${result.reason}`,
          description: result.friendUsername 
            ? `Joining ${result.friendUsername}'s table!`
            : `${result.blinds} blinds • ${result.playerCount}/${result.maxSeats} players`,
          status: 'success',
          duration: 2000,
        });

        // Navigate to the table
        navigate(`/game/${result.tableId}`);
      } else {
        toast({
          title: 'No Tables Found',

          description: result.reason || 'Try creating a new table',
          status: 'info',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('❌ [TableBrowser] Quick Match error:', error);
      toast({
        title: 'Error',

        description: 'Failed to find a table. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsQuickMatching(false);
    }
  }, [currentUser, navigate, toast]);

  //  Filtered tables

  const filteredPlaycoinTables = filterTables(playcoinTables);
  const filteredPkcashTables = filterTables(pkcashTables);
  const totalTables = filteredPlaycoinTables.length + filteredPkcashTables.length;

  if (loading) {
    return (
      <Container maxW="1200px" py={8}>
        <VStack spacing={4} align="center" justify="center" minH="50vh">
          <Spinner color={POKER_COLORS.cyan} size="xl" speed="0.8" />
          <Text color={POKER_COLORS.cyan} display="flex" alignItems="center" gap={2}>
            <Zap size={16} /> Loading tables...
          </Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="1400px" py={8} px={{ base: 4, md: 8 }}>
      <VStack spacing={8} align="stretch">
        {/* Header - Stack vertically on mobile */}
        <VStack 
          spacing={4} 
          align={{ base: 'stretch', md: 'stretch' }}
          w="100%"
        >
          <HStack justify="space-between" align="start" flexWrap="wrap" gap={4}>
            <Box>
              <Heading
                size={{ base: 'md', md: 'lg' }}
                color={POKER_COLORS.cyan}
                mb={2}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Gamepad2 size={28} />
                Poker Tables
              </Heading>
              <Text color={POKER_COLORS.accentCyan} fontSize={{ base: 'sm', md: 'md' }}>
                {totalTables} live tables • Real-time updates
              </Text>
            </Box>
          </HStack>
          
          {/*  FIXED: Horizontally scrollable button row on mobile */}

          <Box
            overflowX="auto"
            overflowY="hidden"
            w="100%"
            pb={2}
            css={{
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <HStack spacing={3} flexShrink={0} minW="max-content">
              {/*  PHASE 2: Quick Match Button */}

              <PokerButton 
                variant="primary" 
                onClick={handleQuickMatch}
                isLoading={isQuickMatching}
                loadingText="Finding..."
                size={{ base: 'md', md: 'lg' }}
                bg="linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                border="2px solid"
                borderColor="green.400"
                flexShrink={0}
                _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}
                data-tutorial="quick-match"
              >
                <Target size={18} />
                Quick Match
              </PokerButton>
              {/*  Join Private Button */}

              <PokerButton 
                variant="secondary" 
                onClick={openPrivateModal} 
                size={{ base: 'md', md: 'lg' }}
                flexShrink={0}
                data-tutorial="private-code"
              >
                <Lock size={18} />
                Join Private
              </PokerButton>
              {/*  Navigate to CreateTablePage */}

              <PokerButton
                variant="secondary"
                onClick={() => navigate('/tables/create?mode=home')}
                size={{ base: 'md', md: 'lg' }}
                flexShrink={0}
              >
                Home Game
              </PokerButton>
              <PokerButton
                variant="secondary"
                onClick={() => navigate('/groups')}
                size={{ base: 'md', md: 'lg' }}
                flexShrink={0}
              >
                Groups
              </PokerButton>
              <PokerButton 
                variant="primary" 
                onClick={() => navigate('/tables/create')} 
                size={{ base: 'md', md: 'lg' }}
                flexShrink={0}
                data-tutorial="create-table"
              >
                <Plus size={20} />
                New Table
              </PokerButton>
            </HStack>
          </Box>
        </VStack>

        {/* Featured-tables section removed - featured tables now show in
            the stake-grouped lobby below alongside any user-created tables,
            matching CoinPoker's single unified layout. The previous
            standalone FeaturedTablesSection duplicated the same tables.
            Re-add if a separate "highlight" banner is wanted later. */}

        {/*  Search Bar */}

        <Box data-tutorial="table-filters">
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
              <Search size={20} color={POKER_COLORS.accentCyan} />
            </InputLeftElement>
            <Input
              placeholder="Search by table name or blinds (e.g., 1/2)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={POKER_COLORS.darkBase}
              color="white"
              border="1px solid"
              borderColor={POKER_COLORS.cyan}
              _placeholder={{ color: POKER_COLORS.accentCyan }}
              _focus={{ borderColor: POKER_COLORS.cyan, boxShadow: `0 0 0 1px ${POKER_COLORS.cyan}` }}
            />
          </InputGroup>
        </Box>

        {/*  NEW: Friends at Tables Section */}

        {friendsAtTables.length > 0 ? (
          <Box
            p={4}
            bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.9) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor="purple.600"
          >
            <HStack justify="space-between" mb={4}>
              <HStack spacing={2}>
                <Text fontSize="xl"></Text>

                <Heading size="md" color="purple.400">
                  Friends Playing ({friendsAtTables.length})
                </Heading>
              </HStack>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {friendsAtTables.map((friend) => (
                <Box
                  key={`${friend.odid}-${friend.tableId}`}
                  p={4}
                  bg="rgba(0,0,0,0.4)"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="purple.700"
                  _hover={{ borderColor: 'purple.500', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  <HStack spacing={3} mb={3}>
                    <Box
                      w="44px"
                      h="44px"
                      borderRadius="full"
                      bg="purple.600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      border="2px solid"
                      borderColor="purple.400"
                    >
                      {friend.photoURL ? (
                        <img
                          src={friend.photoURL}
                          alt={friend.username}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Text color="white" fontWeight="bold" fontSize="lg">
                          {friend.username.charAt(0).toUpperCase()}
                        </Text>
                      )}
                    </Box>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text color="white" fontWeight="bold" fontSize="sm">{friend.username}</Text>
                      <Text color="gray.400" fontSize="xs" isTruncated maxW="120px">{friend.tableName}</Text>
                    </VStack>
                  </HStack>
                  
                  <HStack justify="space-between" mb={3}>
                    <VStack align="start" spacing={0}>
                      <Text color="gray.500" fontSize="xs">Stakes</Text>
                      <Text color={friend.currency === 'pkcash' ? 'yellow.400' : 'cyan.400'} fontWeight="bold" fontSize="sm">
                        {friend.smallBlind}/{friend.bigBlind} {friend.currency === 'pkcash' ? '' : ''}

                      </Text>
                    </VStack>
                    <VStack align="end" spacing={0}>
                      <Text color="gray.500" fontSize="xs">Players</Text>
                      <Text color="white" fontWeight="bold" fontSize="sm">{friend.playerCount}/{friend.maxSeats}</Text>
                    </VStack>
                  </HStack>
                  
                  <PokerButton
                    variant="primary"
                    size="sm"
                    w="100%"
                    onClick={() => navigate(`/game/${friend.tableId}`)}
                    isDisabled={joiningTableId === friend.tableId}
                  >
                    {friend.playerCount >= friend.maxSeats ? ' Spectate' : ' Join Table'}

                  </PokerButton>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ) : (
          <Box
            p={4}
            bg="linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(13,27,42,0.5) 100%)"
            borderRadius="xl"
            border="1px dashed"
            borderColor="purple.800"
          >
            <HStack spacing={2} justify="center">
              <Text fontSize="xl"></Text>

              <Text color="gray.500" fontSize="sm">
                No friends playing right now
              </Text>
            </HStack>
          </Box>
        )}

        {/* ── STAKE-GROUPED VIEW (default) - collapses N tables per stake
              level into one row showing total players + min buy-in. Tap a
              row to drill into the table grid for that stake. */}
        {selectedStake === null && (
          <>
            {filteredPlaycoinTables.length > 0 && (
              /* data-tutorial: the tour's "cash tables" stop. Its old anchor
                 lived in TablesPage, which is not what /tables renders - the
                 tour waited for it forever. */
              <Box data-tutorial="table-list">
                <Heading size="md" color={POKER_COLORS.cyan} mb={4}>
                   PlayCoin Stakes

                </Heading>
                <StakeGroupedView
                  tables={filteredPlaycoinTables}
                  currencyLabel="PlayCoin"
                  currencySymbol=""
                  onSelectStake={setSelectedStake}
                />
              </Box>
            )}
            {filteredPkcashTables.length > 0 && (
              <Box>
                <Heading size="md" color={POKER_COLORS.cyan} mb={4} mt={6}>
                   PSS Coins Stakes

                </Heading>
                <StakeGroupedView
                  tables={filteredPkcashTables}
                  currencyLabel="PSS Coin"
                  currencySymbol="$"
                  onSelectStake={setSelectedStake}
                />
              </Box>
            )}
          </>
        )}

        {/* ── STAKE DRILL-DOWN - show the original table-card grid filtered
              to the chosen stake. Back chevron returns to the grouped view. */}
        {selectedStake !== null && (() => {
          const all = selectedStake.currency === 'playcoins'
            ? filteredPlaycoinTables
            : filteredPkcashTables;
          const tablesAtStake = all.filter(t =>
            t.smallBlind === selectedStake.smallBlind &&
            t.bigBlind === selectedStake.bigBlind &&
            ((t as any).currency || 'playcoins') === selectedStake.currency
          );
          const sym = selectedStake.currency === 'pkcash' ? '$' : '';
          const ccyLabel = selectedStake.currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins';
          return (
            <Box>
              <HStack mb={4} spacing={3}>
                <PokerButton
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedStake(null)}
                  leftIcon={<span style={{ fontSize: 16 }}>←</span>}
                >
                  All stakes
                </PokerButton>
                <Heading size="md" color={POKER_COLORS.cyan}>
                  {sym}{selectedStake.smallBlind} / {sym}{selectedStake.bigBlind} {ccyLabel}
                  <Text as="span" fontSize="sm" color="gray.400" ml={2} fontWeight="normal">
                    ({tablesAtStake.length} {tablesAtStake.length === 1 ? 'table' : 'tables'})
                  </Text>
                </Heading>
              </HStack>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                {tablesAtStake.map((table) => (
                  <PokerTableCard
                    key={table.id}
                    table={table}
                    onJoin={handleJoinTable}
                    isJoining={joiningTableId === table.id}
                  />
                ))}
              </SimpleGrid>
            </Box>
          );
        })()}

        {/* Empty State */}
        {totalTables === 0 && (
          <PokerCard p={12} textAlign="center">
            <VStack spacing={4}>
              <Gamepad2 size={48} color={POKER_COLORS.cyan} />
              <Heading size="md" color={POKER_COLORS.cyan}>
                {searchQuery ? 'No Tables Match Your Search' : 'No Tables Available'}
              </Heading>
              <Text color={POKER_COLORS.accentCyan}>
                {searchQuery ? 'Try a different search term' : 'Create one to get started! '}

              </Text>
              {!searchQuery && (
                <PokerButton variant="primary" onClick={() => navigate('/tables/create')}>
                  Create First Table
                </PokerButton>
              )}
            </VStack>
          </PokerCard>
        )}
      </VStack>

      {/*  Join Private Table Modal */}

      <Modal isOpen={isPrivateModalOpen} onClose={closePrivateModal} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkBase} border="1px solid" borderColor={POKER_COLORS.cyan}>
          <ModalHeader color={POKER_COLORS.cyan}>
            <HStack>
              <Lock size={20} />
              <Text>Join Private Table</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={POKER_COLORS.cyan} />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                Enter the 6-digit code to join a private table
              </Text>
              <Input
                placeholder="Enter code (e.g., ABC123)"
                value={privateCode}
                onChange={(e) => setPrivateCode(e.target.value.toUpperCase())}
                maxLength={20}
                textAlign="center"
                fontSize="2xl"
                letterSpacing="0.3em"
                fontFamily="mono"
                fontWeight="bold"
                color="white"
                bg="rgba(0, 217, 217, 0.1)"
                border="2px solid"
                borderColor={POKER_COLORS.cyan}
                _focus={{ borderColor: POKER_COLORS.cyan, boxShadow: `0 0 0 1px ${POKER_COLORS.cyan}` }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && privateCode.trim()) {
                    handleJoinPrivateTable();
                  }
                }}
              />
              <PokerButton
                variant="primary"
                w="100%"
                onClick={handleJoinPrivateTable}
                isLoading={joiningPrivate}
                isDisabled={!privateCode.trim()}
              >
                Join Table
              </PokerButton>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default TableBrowserPage;